import { createTimeline, stagger } from '../../vendor/anime.esm.js';
import { DURATION, EASE, SPRING_PRESETS, resolveDuration, resolveStagger, BLUR } from '../motion-tokens.js';

export const pricingV1 = (el) => {
    const allCards  = [...el.querySelectorAll('.pricing-card, .card')];
    const section   = el.querySelector('section');
    const accentRaw = section ? getComputedStyle(section).getPropertyValue('--accent').trim() : '#6366f1';
    const featured = allCards.find(c => c.style.transform?.includes('scale(1.05)')) || null;
    const rest     = allCards.filter(c => c !== featured);
    const prices   = el.querySelectorAll('[data-field="price"]');
    const ctas     = el.querySelectorAll('.btn-action');
    const tl = createTimeline({ defaults: { ease: EASE.snappy }, });

    allCards.forEach(c => { c.style.opacity = '0'; c.style.transform = 'translateY(25px)'; c.style.filter = BLUR.light; });
    prices.forEach(p => { p.style.opacity = '0'; p.style.transform = 'translateY(-15px) scaleY(1.15)'; });
    ctas.forEach(b => { b.style.opacity = '0'; b.style.transform = 'translateY(8px)'; });

    if (featured) {
        tl.add(featured, {
            opacity: [0, 1], translateY: [25, 0], filter: [BLUR.normal, BLUR.none],
            duration: resolveDuration(DURATION.normal), ease: EASE.refined,
        });
        tl.add(featured, {
            boxShadow: [
                `0 0 0px 0px transparent`,
                `0 0 40px 8px color-mix(in srgb, ${accentRaw} 35%, transparent)`,
                `0 20px 50px color-mix(in srgb, ${accentRaw} 20%, transparent)`,
            ],
            duration: 550,
        }, '-=400');
    }

    if (rest.length) tl.add(rest, {
        opacity: [0, 1], translateY: [25, 0], filter: [BLUR.light, BLUR.none],
        delay: stagger(resolveStagger(80), { from: 'center' }),
        duration: resolveDuration(DURATION.fast),
    }, featured ? '-=500' : 0);

    if (prices.length) tl.add(prices, {
        opacity: [0, 1], translateY: [-15, 0], scaleY: [1.15, 1], transformOrigin: '50% 0%',
        delay: stagger(resolveStagger(60)),
        duration: resolveDuration(DURATION.fast * 0.8),
        ease: SPRING_PRESETS.magnetic,
    }, '-=500');

    if (ctas.length) tl.add(ctas, {
        opacity: [0, 1], translateY: [8, 0],
        delay: stagger(resolveStagger(50)),
        duration: resolveDuration(DURATION.fast * 0.7),
        ease: SPRING_PRESETS.magnetic,
    }, '-=350');

    return tl;
};

export const pricingV2 = (el) => {
    const cards  = el.querySelectorAll('.pricing-card, .card');
    const prices = el.querySelectorAll('[data-field="price"]');
    const ctas   = el.querySelectorAll('.btn-action');
    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    cards.forEach(c => { c.style.opacity = '0'; c.style.transform = 'translateY(20px)'; c.style.filter = BLUR.light; });
    prices.forEach(p => { p.style.opacity = '0'; p.style.transform = 'translateY(-12px) scaleY(1.12)'; });
    ctas.forEach(b => { b.style.opacity = '0'; });

    if (cards.length) tl.add(cards, {
        opacity: [0, 1], translateY: [20, 0], filter: [BLUR.light, BLUR.none],
        delay: stagger(resolveStagger(80)),
        duration: resolveDuration(DURATION.normal),
    });

    if (prices.length) tl.add(prices, {
        opacity: [0, 1], translateY: [-12, 0], scaleY: [1.12, 1], transformOrigin: '50% 0%',
        delay: stagger(resolveStagger(60)),
        duration: resolveDuration(DURATION.fast * 0.8),
        ease: SPRING_PRESETS.magnetic,
    }, '-=700');

    if (ctas.length) tl.add(ctas, {
        opacity: [0, 1],
        delay: stagger(resolveStagger(50)),
        duration: resolveDuration(DURATION.fast * 0.7),
    }, '-=400');

    return tl;
};

export const pricingV3 = (el) => {
    const allCards = [...el.querySelectorAll('.pricing-card, .card')];
    if (!allCards.length) return null;

    const container = el.querySelector('section') || el;
    container.style.perspective = '1200px';

    const mid    = Math.floor(allCards.length / 2);
    const prices = el.querySelectorAll('[data-field="price"]');
    const ctas   = el.querySelectorAll('.btn-action');
    const tl = createTimeline({ defaults: { ease: EASE.snappy }, });

    allCards.forEach(c => { c.style.opacity = '0'; c.style.transform = 'translateY(30px) rotateX(-6deg)'; c.style.filter = BLUR.light; });
    prices.forEach(p => { p.style.opacity = '0'; p.style.transform = 'translateY(-15px) scaleY(1.15)'; });
    ctas.forEach(b => { b.style.opacity = '0'; b.style.transform = 'translateY(8px)'; });

    tl.add(allCards, {
        opacity: [0, 1],
        translateY: (card, i) => [i === mid ? 45 : 25, 0],
        rotateX: [-6, 0],
        filter: [BLUR.light, BLUR.none],
        delay: stagger(resolveStagger(60), { from: 'center' }),
        duration: (card, i) => resolveDuration(i === mid ? DURATION.normal : DURATION.fast),
    });

    if (prices.length) tl.add(prices, {
        opacity: [0, 1], translateY: [-15, 0], scaleY: [1.15, 1], transformOrigin: '50% 0%',
        delay: stagger(resolveStagger(50), { from: 'center' }),
        duration: resolveDuration(DURATION.fast * 0.8),
        ease: SPRING_PRESETS.magnetic,
    }, '-=600');

    if (ctas.length) tl.add(ctas, {
        opacity: [0, 1], translateY: [8, 0],
        delay: stagger(resolveStagger(50), { from: 'center' }),
        duration: resolveDuration(DURATION.fast * 0.7),
        ease: SPRING_PRESETS.magnetic,
    }, '-=350');

    return tl;
};
